// The artifact pane's API on SENTINEL's NomOS session (the pane itself is Poiesis's, @poiesis/agentArtifacts).
// Every call, including the bridge's Query API questions, rides the signed-in person's own token: a live
// artifact page therefore sees exactly what this person may see, no more.
import '@/pages/work/poiesisHost'   // wires Poiesis's HTTP onto Sentinel's NomOS session (configureHttp)
import { reduce } from '@poiesis/api/reduce'
import type { ReduceRequest } from '@poiesis/views/contract'
import type {
  ArtifactApi, ArtifactPageState, ArtifactState, PageApi, PageCandidates, PageDetail, PageSummary, ThreadFile,
} from '@poiesis/agentArtifacts/types'
import { saveBlob } from '@/lib/download'
import { getConfig, nomosBytes, nomosFetch, type NomosEnvelope } from '@/services/nomos/client'

const thread = (id: string) => `/daimon/threads/${encodeURIComponent(id)}`
const status = (e: unknown) => (e as { response?: { status?: number } } | null)?.response?.status

export const artifactApi: ArtifactApi = {
  async get(threadId) {
    try {
      return (await nomosFetch<NomosEnvelope<ArtifactState>>('GET', `${thread(threadId)}/artifact`)).data
    } catch (e) {
      if (status(e) === 404) return null
      throw e
    }
  },
  async content(threadId, version) {
    const path = version == null ? 'artifact/content' : `artifact/versions/${version}/content`
    return (await nomosBytes('GET', `${thread(threadId)}/${path}`, { accept: 'text/html' })).blob.text()
  },
  async restore(threadId, version) {
    return (await nomosFetch<NomosEnvelope<ArtifactState>>('POST', `${thread(threadId)}/artifact/versions/${version}/restore`, {})).data
  },
  async files(threadId) {
    return (await nomosFetch<NomosEnvelope<{ files: ThreadFile[] }>>('GET', `${thread(threadId)}/files`)).data.files
  },
  async download(file) {
    const { blob } = await nomosBytes('GET', `/daimon/files/${encodeURIComponent(file.file_id)}/content`)
    saveBlob(blob, file.name)
  },
  runQuery: (question) => reduce(getConfig().tenant, question as ReduceRequest),
}

/** The artifact's own window (opened from the pane): the same page, full size, in its own tab. */
export function artifactWindowUrl(threadId: string): string {
  return `/coo/artifact/${encodeURIComponent(threadId)}`
}

// ── Pages: an artifact pinned and shared (monad/agent/pages.py) ─────────────────────────────

/** The server's words for a refusal (Plenum error envelope), else a plain sentence. */
function refusal(e: unknown, fallback: string): Error {
  const msg = (e as { response?: { data?: { error?: { message?: unknown } } } } | null)?.response?.data?.error?.message
  return new Error(typeof msg === 'string' && msg ? msg : fallback)
}

/** Where a pinned page opens. */
export function pageHref(pageId: string): string {
  return `/pages/${encodeURIComponent(pageId)}`
}

/** Pin and share, for the artifact's owner (the chat pane). */
export const pageApi: PageApi = {
  async publish(threadId, audience) {
    try {
      return (await nomosFetch<NomosEnvelope<ArtifactPageState & { page_id: string }>>('PUT', `${thread(threadId)}/artifact/page`, audience)).data
    } catch (e) { throw refusal(e, 'The page could not be saved') }
  },
  async unpublish(threadId) {
    await nomosFetch('DELETE', `${thread(threadId)}/artifact/page`)
  },
  async audience() {
    return (await nomosFetch<NomosEnvelope<PageCandidates>>('GET', '/daimon/pages/audience')).data
  },
  pageHref,
}

const page = (id: string) => `/daimon/pages/${encodeURIComponent(id)}`

/** Reading pages: the sidebar's lists, one page, its newest version. A page not shared with you is "not found". */
export const pagesApi = {
  async list(): Promise<{ mine: PageSummary[]; shared: PageSummary[] }> {
    return (await nomosFetch<NomosEnvelope<{ mine: PageSummary[]; shared: PageSummary[] }>>('GET', '/daimon/pages')).data
  },
  async get(pageId: string): Promise<PageDetail | null> {
    try {
      return (await nomosFetch<NomosEnvelope<PageDetail>>('GET', page(pageId))).data
    } catch (e) {
      if (status(e) === 404) return null
      throw e
    }
  },
  async content(pageId: string): Promise<string> {
    return (await nomosBytes('GET', `${page(pageId)}/content`, { accept: 'text/html' })).blob.text()
  },
  runQuery: (question: unknown) => artifactApi.runQuery(question),
}
